/* eslint-disable max-lines */
import { css } from 'styled-components';
import { containerOrientations, containerSpacingModes } from './utils';
import { containerAlignment } from './utils';
//
// Alignment Mixin
//
// Alignment CSS settings can be a bit tricky to understand.
// To help with this, we've provided a small chart for each block below that uses the following symbols:
//   - `v` represents "vertical" dir, or when items flow from top to bottom.
//   - `>` represents "horizontal" dir, or hwne items flow from left to right.
//   - `X` represents when both dirs overlap for a particular setting.
//
// These symbols are placed in a grid that corresponds to 9 different position settings
// that correllate to the Auto Layout alignment grid in Figma.
//
export const align = ({ align, dir, spacingMode, }) => {
    const { horizontal, vertical } = containerOrientations(dir);
    const { packed, spaceBetween } = containerSpacingModes(spacingMode);
    const { bottom, bottomLeft, bottomRight, center, left, right, top, topLeft, topRight, } = containerAlignment(align, dir, spacingMode);
    //
    // Debugging aid; please leave in place
    //
    // console.log('mixin: align');
    // console.table([
    //   {
    //     label: 'orientation',
    //     value: horizontal ? 'horizontal' : 'vertical',
    //   },
    //   {
    //     label: 'spacing',
    //     value: spaceBetween ? 'space-between' : 'packed',
    //   },
    //   {
    //     label: 'top-left',
    //     value: topLeft,
    //   },
    //   {
    //     label: 'top',
    //     value: top,
    //   },
    //   {
    //     label: 'top-right',
    //     value: topRight,
    //   },
    //   {
    //     label: 'left',
    //     value: left,
    //   },
    //   {
    //     label: 'center',
    //     value: center,
    //   },
    //   {
    //     label: 'right',
    //     value: right,
    //   },
    //   {
    //     label: 'bottom-left',
    //     value: bottomLeft,
    //   },
    //   {
    //     label: 'bottom',
    //     value: bottom,
    //   },
    //   {
    //     label: 'bottom-right',
    //     value: bottomRight,
    //   },
    // ]);
    switch (true) {
        //
        // Space between alignments
        //
        // For these settings, the items will be spaced out to fill the entire space available
        // thanks to `justify-content: space-between`.
        // We show `>`, `v`, and `X` spanning the entire space to help visualize this effect.
        // As a result, only the `align-items` property will change for each group.
        //
        // | X | > | > |
        // | v |   |   |
        // | v |   |   |
        case spaceBetween && horizontal && top:
        case spaceBetween && vertical && left:
            return css `
        justify-content: space-between;
        align-items: flex-start;
      `;
        // |   | v |   |
        // | > | X | > |
        // |   | v |   |
        case spaceBetween && center:
            return css `
        justify-content: space-between;
        align-items: center;
      `;
        // |   |   | v |
        // |   |   | v |
        // | > | > | X |
        case spaceBetween && horizontal && bottom:
        case spaceBetween && vertical && right:
            return css `
        justify-content: space-between;
        align-items: flex-end;
      `;
        //
        // Packed alignments
        //
        // For these settings, the items will be starting from one of 9 different positions in the space available.
        // This position is indicated by the symbol
        //
        // | X |   |   |
        // |   |   |   |
        // |   |   |   |
        case packed && topLeft:
            return css `
        justify-content: flex-start;
        align-items: flex-start;
      `;
        // |   | > |   |
        // |   |   |   |
        // |   |   |   |
        case packed && horizontal && top:
            return css `
        justify-content: center;
        align-items: flex-start;
      `;
        // |   | v |   |
        // |   |   |   |
        // |   |   |   |
        case packed && vertical && top:
            return css `
        justify-content: flex-start;
        align-items: center;
      `;
        // |   |   | > |
        // |   |   |   |
        // |   |   |   |
        case packed && horizontal && topRight:
            return css `
        justify-content: flex-end;
        align-items: flex-start;
      `;
        // |   |   | v |
        // |   |   |   |
        // |   |   |   |
        case packed && vertical && topRight:
            return css `
        justify-content: flex-start;
        align-items: flex-end;
      `;
        // |   |   |   |
        // | > |   |   |
        // |   |   |   |
        case packed && horizontal && left:
            return css `
        justify-content: flex-start;
        align-items: center;
      `;
        // |   |   |   |
        // | v |   |   |
        // |   |   |   |
        case packed && vertical && left:
            return css `
        justify-content: center;
        align-items: flex-start;
      `;
        // |   |   |   |
        // |   | X |   |
        // |   |   |   |
        case packed && center:
            return css `
        justify-content: center;
        align-items: center;
      `;
        // |   |   |   |
        // |   |   | > |
        // |   |   |   |
        case packed && horizontal && right:
            return css `
        justify-content: flex-end;
        align-items: center;
      `;
        // |   |   |   |
        // |   |   | v |
        // |   |   |   |
        case packed && vertical && right:
            return css `
        justify-content: center;
        align-items: flex-end;
      `;
        // |   |   |   |
        // |   |   |   |
        // | > |   |   |
        case packed && horizontal && bottomLeft:
            return css `
        justify-content: flex-start;
        align-items: flex-end;
      `;
        // |   |   |   |
        // |   |   |   |
        // | v |   |   |
        case packed && vertical && bottomLeft:
            return css `
        justify-content: flex-end;
        align-items: flex-start;
      `;
        // |   |   |   |
        // |   |   |   |
        // |   | > |   |
        case packed && horizontal && bottom:
            return css `
        justify-content: center;
        align-items: flex-end;
      `;
        // |   |   |   |
        // |   |   |   |
        // |   | v |   |
        case packed && vertical && bottom:
            return css `
        justify-content: flex-end;
        align-items: center;
      `;
        // |   |   |   |
        // |   |   |   |
        // |   |   | X |
        case packed && bottomRight:
            return css `
        justify-content: flex-end;
        align-items: flex-end;
      `;
    }
};
